import { Component, DestroyRef, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { UserHttpService } from '../../core/services/user.http.service';
import { ToastService } from '../../core/services/toast.service';

@Component({
  selector: 'app-reset-password',
  imports: [
    ReactiveFormsModule,
    RouterModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
  ],
  templateUrl: './reset-password.html',
  styleUrl: './reset-password.scss',
})
export class ResetPassword {
  readonly resetForm: FormGroup;
  readonly isSubmitting = signal<boolean>(false);
  readonly errorMessage = signal<string>('');
  readonly successMessage = signal<string>('');

  private readonly token: string;

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private httpService: UserHttpService,
    private destroyRef: DestroyRef,
    private router: Router,
    private toast: ToastService,
  ) {
    this.resetForm = this.fb.group(
      {
        password: ['', [
          Validators.required,
          Validators.pattern(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/),
        ]],
        confirmPassword: ['', [Validators.required]],
      },
      {
        validators: [this.passwordMatchValidator()],
      },
    );

    this.token = new URLSearchParams(String(this.route.snapshot.fragment ?? '')).get('token')?.trim() ?? '';

    if (!this.token) {
      const message = 'Invalid reset link. Request a new password reset email.';
      this.errorMessage.set(message);
      this.toast.error(message);
    }
  }

  onSubmit(): void {
    this.resetForm.markAllAsTouched();
    if (this.resetForm.invalid || !this.token || this.isSubmitting()) {
      return;
    }

    const password = String(this.resetForm.get('password')?.value ?? '');
    this.isSubmitting.set(true);
    this.errorMessage.set('');
    this.successMessage.set('');

    this.httpService
      .resetPassword(this.token, password)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.isSubmitting.set(false);
          const message = String(res?.message ?? 'Password changed successfully.');
          this.successMessage.set(message);
          this.toast.success(message);
          this.router.navigate(['/login']);
          this.resetForm.reset({ password: '', confirmPassword: '' });
        },
        error: (error) => {
          this.isSubmitting.set(false);
          const apiMessage = String(error?.error?.message ?? '').trim();
          const message = apiMessage || 'Password reset failed. Request a new reset link and try again.';
          this.errorMessage.set(message);
          this.toast.error(message);
        },
      });
  }

  private passwordMatchValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const password = String(control.get('password')?.value ?? '');
      const confirmPassword = String(control.get('confirmPassword')?.value ?? '');

      if (!password || !confirmPassword) {
        return null;
      }

      return password === confirmPassword ? null : { passwordMismatch: true };
    };
  }
}
